import { CHAIN_ID } from '@derivation-tech/context';
import { JsonRpcProvider, JsonRpcSigner } from '@ethersproject/providers';
import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { NATIVE_TOKEN_ADDRESS, utils } from '@synfutures/sdks-perp';
import { BigNumber } from 'ethers';
import { parseUnits } from 'viem';

import { BALANCE_TYPE, OPERATION_TX_TYPE } from '@/constants';
import { WrappedBigNumber } from '@/entities/WrappedBigNumber';
import { IGateAccountState } from '@/types/balance';
import { TokenInfo } from '@/types/token';
import { BlockyResult } from '@/types/worker';
import { parseSendingTxMessageMapping, txSendNotification } from '@/utils/notification';
import { toWad } from '@/utils/numberUtil';
import { isNativeTokenAddr } from '@/utils/token';

import { DAPP_CHAIN_CONFIGS } from '@/constants/chain';
import { QUERY_KEYS } from '@/constants/query';
import { queryClient } from '@/global';
import { INotificationArgs } from '@/hooks/useTxNotification';
import i18n from '@/i18n';
import { getSynContract } from '@/utils/contract';
import { sendGelatoTransaction } from '@/utils/gelatoTransaction';
import { Context } from '@derivation-tech/context';

import { useAgentStore } from '../agent/store';
import { sendTransaction } from '../transaction/actions';
import { checkTokenAllowance } from '../user/actions';

export const fetchGateBalanceAction = createAsyncThunk(
  'balance/fetchGateBalanceAction',
  async ({
    chainId,
    userAddr,
  }: {
    chainId: CHAIN_ID;
    userAddr: string;
    tokens: TokenInfo[];
    block?: number;
  }): Promise<
    | BlockyResult<
        {
          token: TokenInfo;
          balance: BigNumber;
          block: number;
        }[]
      >
    | undefined
  > => {
    try {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.BALANCE.GATE(chainId, userAddr),
      });
      return undefined;
    } catch (e) {
      console.log('🚀 ~ file: actions.ts:284 ~ e:', e);
      return undefined;
    }
  },
);

export const depositOrWithdrawBalance = createAsyncThunk(
  'balance/depositOrWithdrawBalance',
  async (
    {
      signer,
      provider,
      quote,
      amount,
      chainId,
      account: userAddr,
      sdkContext,
      balanceType,
      isDisableNotification,
      sendTxNotification,
    }: {
      signer: JsonRpcSigner;
      provider?: JsonRpcProvider;
      quote: TokenInfo;
      account: string;
      amount: string | number | BigNumber;
      chainId: CHAIN_ID;
      sdkContext: Context;
      balanceType: BALANCE_TYPE;
      isDisableNotification?: boolean;
      sendTxNotification?: (args: INotificationArgs) => void;
    },
    { dispatch },
  ) => {
    console.log('🚀 ~ sendTxNotification:', sendTxNotification);
    try {
      const chainConfig = DAPP_CHAIN_CONFIGS[chainId];
      const txType = balanceType === BALANCE_TYPE.DEPOSIT ? OPERATION_TX_TYPE.DEPOSIT : OPERATION_TX_TYPE.WITHDRAW;
      const isNativeToken = isNativeTokenAddr(quote.address);

      const sendingTemplate = parseSendingTxMessageMapping[txType]!(WrappedBigNumber.from(amount), quote.symbol);

      const txParams = {
        isDisableNotification,
        type: txType,
        instrument: {
          quoteSymbol: quote.symbol,
          baseSymbol: '',
          isInverse: false,
          quoteDecimal: quote.decimals,
        },
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        sendingTemplate: sendingTemplate,
      };
      const receipt = await dispatch(
        sendTransaction({
          signer,
          sendFunc: async () => {
            console.record('tx', `Try to send [${txType}] operation tx for quote [${quote.symbol}]`, undefined, {
              chainId,
              userAddr,
              signer,
              quote,
              amount: toWad(amount),
              quoteSymbol: quote.symbol,
              to: userAddr,
              balanceType,
              isNative: isNativeToken,
              nativeTokenAddr: NATIVE_TOKEN_ADDRESS,
            });
            console.log(`Try to send [${txType}] operation tx for quote [${quote.symbol}]`, {
              balanceType,
              userAddr,
              chainId,
              quote,
              quoteAddr: isNativeToken ? NATIVE_TOKEN_ADDRESS : quote.address,
              amount: toWad(amount),
            });
            const agentSettings = useAgentStore.getState().userAgentSettings[userAddr];
            const agentAddr = agentSettings?.agentAddress;
            const isEnableAgentMode = agentSettings?.isEnableAgentMode;
            if (agentAddr && isEnableAgentMode) {
              const routerContract = getSynContract({
                chainId,
                type: 'router',
                signerOrProvider: signer,
              });
              const gateContract = getSynContract({
                chainId,
                type: 'gate',
                signerOrProvider: signer,
              });
              if (!routerContract || !gateContract) {
                throw new Error('Router or Gate contract not found');
              }

              const amountWad = BigNumber.from(parseUnits(amount.toString(), quote.decimals).toString());

              const depositArgs = utils.encodeDepositParam(
                isNativeToken ? NATIVE_TOKEN_ADDRESS : quote.address,
                amountWad,
              );
              const targetData = gateContract.interface.encodeFunctionData(
                balanceType === BALANCE_TYPE.DEPOSIT ? 'depositFor' : 'withdrawFor',
                [depositArgs, userAddr],
              );

              const batchData = routerContract.interface.encodeFunctionData('batch', [
                agentAddr, // signer address
                userAddr,
                gateContract.address, // targetContract address
                targetData,
                false, // userPaid
              ]);

              const result = await sendGelatoTransaction({
                chainId,
                batchData: batchData,
                targetContract: routerContract.address,
                userAddr, // Add userAddr parameter
                provider,
                hooks: {
                  onStart: (taskId) => {
                    txSendNotification({
                      taskId,
                      txType,
                      sendingTemplate,
                      sendTxNotification,
                    });
                  },
                  onError: (error, taskId) => {
                    sendTxNotification?.({
                      message: `${i18n.t(`notification.${txType}.error`)}`,
                      description: error || `${i18n.t(`notification.${txType}.errorDesc`)}`,
                      taskId,
                      type: 'error',
                    });
                  },
                },
              });
              if (result.success && result.taskId && result?.response) {
                return { taskId: result.taskId, txResult: result.response };
              }
            } else {
              const populatedTx = await (balanceType === BALANCE_TYPE.DEPOSIT
                ? sdkContext.perp.gate.depositWad(isNativeToken ? NATIVE_TOKEN_ADDRESS : quote.address, toWad(amount), {
                    from: userAddr,
                  })
                : sdkContext.perp.gate.withdrawWad(
                    isNativeToken ? NATIVE_TOKEN_ADDRESS : quote.address,
                    toWad(amount),
                    {
                      from: userAddr,
                    },
                  ));

              return signer.sendTransaction(populatedTx);
            }
          },
          // needAddTx: !(agentAddr && isEnableAgentMode),
          chainId,
          userAddr,
          txParams: txParams,
          wssProvider: provider,
        }),
      ).unwrap();

      if (receipt?.status === 1) {
        const block = receipt.blockNumber;
        if (balanceType === BALANCE_TYPE.DEPOSIT) {
          dispatch(
            checkTokenAllowance({
              userAddress: userAddr,
              chainId,
              marginToken: quote,
              sdk: sdkContext,
            }),
          );
        }
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.BALANCE.TOKEN_BALANCE(chainId, userAddr),
        });
        // dispatch(fetchTokenBalanceAction({ chainId, userAddr: userAddr, token: quote, block }));
        chainConfig.wrappedNativeToken &&
          dispatch(
            fetchGateBalanceAction({
              chainId,
              userAddr,
              tokens: isNativeToken ? [chainConfig.wrappedNativeToken] : [quote],
              block: block,
            }),
          );
      }
      return receipt;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      console.error('balance depositOrWithdrawBalance error', e);
      throw e;
    }
  },
);

export const setGateAccountState = createAction<Partial<IGateAccountState> & { chainId: number }>(
  'balance/setGateAccountState',
);
